import type { LicenseData } from "@/hooks/useLicenseStore"
import { strings } from "@/strings"
import type { NormalizedLicenseData } from "./normalizeLicenseData"

export type LicenseIssue = {
  field: string
  message: string
  severity: "info" | "warning" | "error"
}

const asYesNo = (value: unknown): "yes" | "no" | undefined => {
  if (typeof value === "boolean") return value ? "yes" : "no"
  if (typeof value !== "string") return undefined
  const normalized = value.trim().toLowerCase()
  if (normalized === "yes" || normalized === "no") return normalized
  return undefined
}

const asBool = (value: unknown): boolean => {
  if (typeof value === "boolean") return value
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (["true", "yes", "1", "on"].includes(normalized)) return true
  }
  return false
}

const hasAnySelection = (value: unknown): boolean => {
  if (!value || typeof value !== "object") return false
  return Object.values(value as Record<string, boolean>).some(Boolean)
}

export const validateLicenseData = (
  input: LicenseData,
  normalized: NormalizedLicenseData
): LicenseIssue[] => {
  const issues: LicenseIssue[] = []
  const addIssue = (field: string, message: string) => {
    issues.push({ field, message, severity: "error" })
  }

  if (normalized.hasExpiry && !normalized.expiryDate) {
    addIssue("expiryDate", "Add an expiry date for this license.")
  }

  if (!hasAnySelection(input.accessMode)) {
    addIssue("accessMode", "Select at least one sharing type.")
  }

  const mandatoryRadios: Array<{ field: string; label: string }> = [
    { field: "commercialUse", label: strings.title.commercialUsePrompt },
    { field: "onwardSharing", label: strings.title.onwardSharingPrompt },
    { field: "publishingPermitted", label: "Publishing allowed" },
    { field: "attributionRequired", label: "Attribution required" }
  ]

  mandatoryRadios.forEach(({ field, label }) => {
    if (!asYesNo(input[field])) {
      addIssue(field, `Select an option for ${label}.`)
    }
  })

  const mandatoryChecks: Array<{ field: string; message: string }> = [
    {
      field: "acceptedAuthorization",
      message: "Confirm that it is appropriate to issue the data license."
    },
    {
      field: "acceptedMutability",
      message: "Confirm that this license is irrevocable and immutable."
    },
    {
      field: "acceptedTerms",
      message: "Confirm you have read and understand the license terms."
    }
  ]

  mandatoryChecks.forEach(({ field, message }) => {
    if (!asBool(input[field])) {
      addIssue(field, message)
    }
  })

  return issues
}
